import { ComponentProps, useCallback, useState } from 'react';

import { endOfDay, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';

import { MAX_DATE, MIN_DATE, clampTo, getValidDate, setDateWithoutChangingTime } from '../utils';

import { DateBox } from './DateBox';
import { Preset } from './Preset';
import { TimeBox } from './TimeBox';

interface ModalCommonProps {
  readonly startDate?: Date;
  readonly allDay?: boolean;
  readonly timePicker?: boolean;
  readonly dates?: ComponentProps<typeof DateBox>['dates'];
}

interface ModalSingleProps {
  readonly rangePicker?: false;
  readonly endDate?: never;
  readonly onChangeDate?: (startDate: Date, allDay: boolean) => void;
}

interface ModalRangeProps {
  readonly rangePicker: true;
  readonly endDate?: Date;
  readonly onChangeDate?: (startDate: Date, endDate: Date, allDay: boolean) => void;
}

type ModalProps = ModalCommonProps & (ModalSingleProps | ModalRangeProps);

function Modal({ startDate, endDate, allDay, rangePicker, dates, onChangeDate, timePicker }: ModalProps) {
  const minDate = dates?.[0] ?? MIN_DATE;
  const maxDate = dates?.[1] ?? MAX_DATE;
  const [start, setStart] = useState(startDate || getValidDate());
  const [end, setEnd] = useState(endDate || getValidDate());
  const [checked, setChecked] = useState(!!allDay);
  const [, setPointer] = useState(true);

  const clamp = useCallback(
    (date: Date) => {
      return clampTo(date, {
        start: minDate,
        end: maxDate,
      });
    },
    [minDate, maxDate],
  );

  const onApplyChange = useCallback(() => {
    if (rangePicker) {
      onChangeDate?.(start, end, checked);
    } else {
      onChangeDate?.(start, checked);
    }
  }, [rangePicker, onChangeDate, start, end, checked]);

  const onChangeByPreset = useCallback(
    (_startDate: Date, _endDate: Date) => {
      const _start = clamp(new Date(_startDate));
      const _end = clamp(new Date(_endDate));

      setStart(_start);
      setEnd(_end);
      setChecked(true);

      if (rangePicker) {
        onChangeDate?.(_start, _end, checked);
      }
    },
    [clamp, checked, rangePicker, onChangeDate],
  );

  const onPickDate = useCallback(
    (date: Date) => {
      if (rangePicker) {
        setPointer((pointer) => {
          if (pointer) {
            // Setting start
            setStart((_start) => clamp(setDateWithoutChangingTime(_start, date)));
            setEnd((_end) => clamp(setDateWithoutChangingTime(_end, date)));
          } else {
            // Setting end
            if (isSameDay(date, start)) {
              // Do nothing
              return pointer;
            }

            if (isBefore(date, start)) {
              setStart((_start) => clamp(setDateWithoutChangingTime(_start, date)));
              setEnd((_end) => clamp(setDateWithoutChangingTime(_end, start)));
            } else {
              setEnd((_end) => clamp(setDateWithoutChangingTime(_end, date)));
            }
          }

          return !pointer;
        });
      } else {
        setStart((_start) => clamp(setDateWithoutChangingTime(_start, date)));
      }
    },
    [rangePicker, start, clamp],
  );

  const onChangeDateByInput = useCallback(
    (_startDate: Date, _endDate?: Date) => {
      const newStart = clamp(_startDate);
      const newEnd = clamp(_endDate || end);

      if (isAfter(newStart, newEnd)) {
        setStart(newEnd);
        setEnd(newStart);
      } else {
        setStart(newStart);

        if (_endDate) {
          setEnd(newEnd);
        }
      }
    },
    [clamp, end],
  );

  const onToggleAllDay = useCallback(
    (ad: boolean) => {
      setChecked(ad);

      if (ad && rangePicker) {
        onChangeDateByInput(startOfDay(start), endOfDay(end));
      }
    },
    [start, end, rangePicker, onChangeDateByInput],
  );

  return (
    <div
      role="dialog"
      data-testid="date-picker-dialog"
      className="flex flex-row rounded-xl bg-white text-sm text-neutral-800 drop-shadow-md"
    >
      {rangePicker ? <Preset onClick={onChangeByPreset} className="w-[158px]" /> : null}
      {rangePicker ? (
        <>
          <DateBox rangePicker startDate={start} endDate={end} dates={dates} onPickDate={onPickDate} />
          <TimeBox
            rangePicker
            timePicker={timePicker}
            startDate={start}
            endDate={end}
            allDay={checked}
            onToggleAllDay={onToggleAllDay}
            onChangeDate={onChangeDateByInput}
            onApply={onApplyChange}
            className="w-56"
          />
        </>
      ) : (
        <>
          <DateBox startDate={start} dates={dates} onPickDate={onPickDate} className="pl-6" />
          <TimeBox
            timePicker={timePicker}
            startDate={start}
            allDay={checked}
            onToggleAllDay={onToggleAllDay}
            onChangeDate={onChangeDateByInput}
            onApply={onApplyChange}
            className="w-56"
          />
        </>
      )}
    </div>
  );
}

export { Modal };
